import CheckIcon from "@redotech/redo-icons/check.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import ThumbsDownIcon from "@redotech/redo-icons/thumbs-down.svg";
import ThumbsDownFilledIcon from "@redotech/redo-icons/thumbs-down_filled.svg";
import ThumbsUpIcon from "@redotech/redo-icons/thumbs-up.svg";
import ThumbsUpFilledIcon from "@redotech/redo-icons/thumbs-up_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { copyToClipboard } from "@redotech/redo-web/clipboard";
import { Flex } from "@redotech/redo-web/flex";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useCallback, useState } from "react";

/**
 * Per-message utility row shown under each completed assistant
 * message: thumbs up / thumbs down / copy. Local UI only — thumbs
 * are a visual toggle; copy writes the message's combined text to
 * the clipboard. Wire to telemetry later if needed.
 */
export interface AskRedoMessageActionsProps {
  /** Plain-text representation of the assistant's reply, used by the
   *  copy button. Empty string is fine — the copy button stays
   *  rendered but no-ops. */
  text: string;
  /** Optional extra class on the outer Flex — the parent message
   *  uses this to reveal the row on hover/focus or keep it visible
   *  on touch devices. */
  className?: string;
}

export const AskRedoMessageActions = memo(function AskRedoMessageActions({
  text,
  className,
}: AskRedoMessageActionsProps) {
  const [feedback, setFeedback] = useState<"positive" | "negative" | undefined>(
    undefined,
  );
  const [copied, setCopied] = useState(false);

  const toggleFeedback = useCallback((next: "positive" | "negative") => {
    setFeedback((prev) => (prev === next ? undefined : next));
  }, []);

  const handleCopy = useCallback(async () => {
    if (!text) {
      return;
    }
    await copyToClipboard(text);
    setCopied(true);
    // Reset the check-icon flash after a moment so the copy button
    // returns to its default affordance. setState on an unmounted
    // component is a no-op in React 18, so no cleanup is required.
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <Flex align="center" className={className} gap="xxs">
      <Tooltip title={copied ? "Copied" : "Copy message"}>
        <Flex align="center" as="span" gap="none">
          <RedoButton
            aria-label={copied ? "Copied" : "Copy message"}
            hierarchy="tertiary"
            IconLeading={copied ? CheckIcon : CopyIcon}
            onClick={handleCopy}
            size="xs"
          />
        </Flex>
      </Tooltip>
      <Tooltip title="Good response">
        <Flex align="center" as="span" gap="none">
          <RedoButton
            aria-label="Good response"
            hierarchy="tertiary"
            IconLeading={
              feedback === "positive" ? ThumbsUpFilledIcon : ThumbsUpIcon
            }
            onClick={() => toggleFeedback("positive")}
            size="xs"
          />
        </Flex>
      </Tooltip>
      <Tooltip title="Bad response">
        <Flex align="center" as="span" gap="none">
          <RedoButton
            aria-label="Bad response"
            hierarchy="tertiary"
            IconLeading={
              feedback === "negative" ? ThumbsDownFilledIcon : ThumbsDownIcon
            }
            onClick={() => toggleFeedback("negative")}
            size="xs"
          />
        </Flex>
      </Tooltip>
    </Flex>
  );
});
